/**
 * Visual Prompt Editor
 * XML-tree style visual editor for prompt templates
 * Shows the prompt structure as a hierarchical tree with inline editing
 */

import { useState, useMemo } from 'react';
import {
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Code,
  FileText,
  Zap,
  Link2,
  Clock,
  Settings2,
  Copy,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { PromptSection, SectionType } from '@/types/promptTemplates';
import { SECTION_TYPE_INFO, AVAILABLE_SOURCES } from '@/types/promptTemplates';

// Section type icons
const SECTION_TYPE_ICONS: Record<SectionType, React.ComponentType<{ className?: string }>> = {
  static: FileText,
  hook: Zap,
  dependency: Link2,
  runtime: Clock,
};

interface VisualPromptEditorProps {
  sections: PromptSection[];
  separator: string;
  onAddSection: () => void;
  onEditSection: (section: PromptSection) => void;
  onDeleteSection: (sectionId: string) => void;
  onToggleSection: (sectionId: string, enabled: boolean) => void;
  onReorder: (sections: PromptSection[]) => void;
}

export function VisualPromptEditor({
  sections,
  separator,
  onAddSection,
  onEditSection,
  onDeleteSection,
  onToggleSection,
  onReorder,
}: VisualPromptEditorProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(sections.map(s => s.id))
  );
  const [showPreview, setShowPreview] = useState(false);
  const [copied, setCopied] = useState(false);

  // Sort sections by order
  const sortedSections = useMemo(
    () => [...sections].sort((a, b) => a.order - b.order),
    [sections]
  );

  const toggleExpand = (sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedSections(new Set(sections.map(s => s.id)));
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
  };

  // Move section up/down
  const moveSection = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sortedSections.length) return;

    const newSections = [...sortedSections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];

    // Update order values
    const updatedSections = newSections.map((section, idx) => ({
      ...section,
      order: (idx + 1) * 10,
    }));

    onReorder(updatedSections);
  };

  // Generate preview XML
  const previewXml = useMemo(() => {
    const enabledSections = sortedSections.filter(s => s.enabled);
    const lines: string[] = [];

    enabledSections.forEach((section, idx) => {
      const tagName = section.wrap_tag || section.id;
      const content = getPlaceholderContent(section);

      lines.push(`<${tagName}>`);
      content.split('\n').forEach(line => {
        lines.push(`  ${line}`);
      });
      lines.push(`</${tagName}>`);

      if (idx < enabledSections.length - 1) {
        if (separator === '\n\n') {
          lines.push('');
        } else if (separator === '\n---\n') {
          lines.push('---');
        }
      }
    });

    return lines.join('\n');
  }, [sortedSections, separator]);

  const copyPreview = async () => {
    await navigator.clipboard.writeText(previewXml);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={expandAll}>
            Expand All
          </Button>
          <Button size="sm" variant="ghost" onClick={collapseAll}>
            Collapse All
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={showPreview ? 'secondary' : 'ghost'}
            onClick={() => setShowPreview(!showPreview)}
          >
            <Code className="w-4 h-4 mr-1" />
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
          <Button size="sm" variant="primary" onClick={onAddSection}>
            <Plus className="w-4 h-4 mr-1" />
            Add Section
          </Button>
        </div>
      </div>

      <div className={`grid gap-4 ${showPreview ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {/* Visual Tree Editor */}
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 font-mono text-sm">
          {/* Root element */}
          <div className="text-purple-600 font-semibold mb-2">
            {'<system_prompt>'}
          </div>

          {/* Sections */}
          <div className="ml-4 space-y-1">
            {sortedSections.length === 0 ? (
              <div className="text-gray-400 italic py-4 text-center">
                No sections defined. Click "Add Section" to create one.
              </div>
            ) : (
              sortedSections.map((section, index) => (
                <XMLSectionNode
                  key={section.id}
                  section={section}
                  index={index}
                  totalCount={sortedSections.length}
                  isExpanded={expandedSections.has(section.id)}
                  onToggleExpand={() => toggleExpand(section.id)}
                  onEdit={() => onEditSection(section)}
                  onDelete={() => onDeleteSection(section.id)}
                  onToggleEnabled={(enabled) => onToggleSection(section.id, enabled)}
                  onMoveUp={() => moveSection(index, 'up')}
                  onMoveDown={() => moveSection(index, 'down')}
                />
              ))
            )}
          </div>

          {/* Root closing tag */}
          <div className="text-purple-600 font-semibold mt-2">
            {'</system_prompt>'}
          </div>
        </div>

        {/* XML Preview */}
        {showPreview && (
          <div className="bg-gray-900 rounded-lg border border-gray-700 p-4 font-mono text-sm overflow-auto max-h-[600px]">
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-700">
              <span className="text-gray-400 text-xs uppercase tracking-wide">
                Preview Output
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={copyPreview}
                className="text-gray-400 hover:text-white"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-400" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
            <pre className="text-green-400 whitespace-pre-wrap">
              {previewXml || '<empty prompt>'}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

// Individual XML section node
interface XMLSectionNodeProps {
  section: PromptSection;
  index: number;
  totalCount: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleEnabled: (enabled: boolean) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function XMLSectionNode({
  section,
  index,
  totalCount,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onToggleEnabled,
  onMoveUp,
  onMoveDown,
}: XMLSectionNodeProps) {
  const typeInfo = SECTION_TYPE_INFO[section.type];
  const TypeIcon = SECTION_TYPE_ICONS[section.type];
  const tagName = section.wrap_tag || section.id;

  return (
    <div
      className={`group rounded transition-colors ${
        section.enabled ? '' : 'opacity-50'
      }`}
    >
      {/* Opening tag line */}
      <div className="flex items-center gap-1 py-1 hover:bg-gray-100 rounded px-1 -mx-1">
        {/* Drag handle (visible on hover) - fixed width for alignment */}
        <div className="w-5 flex items-center justify-center">
          <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={onMoveUp}
              disabled={index === 0}
              className="p-0 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed leading-none"
              title="Move up"
            >
              <ChevronUp className="w-3 h-3" />
            </button>
            <button
              onClick={onMoveDown}
              disabled={index === totalCount - 1}
              className="p-0 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed leading-none -mt-1"
              title="Move down"
            >
              <ChevronDown className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Expand/Collapse toggle */}
        <button
          onClick={onToggleExpand}
          className="p-0.5 text-gray-400 hover:text-gray-600"
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {/* Tag content */}
        <div className="flex-1 flex items-center gap-2">
          {/* Opening tag */}
          <span className="text-blue-600">{'<'}</span>
          <span className="text-red-600 font-medium">{tagName}</span>
          <span className="text-blue-600">{'>'}</span>

          {/* Type badge */}
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${typeInfo.color}`}>
            <TypeIcon className="w-3 h-3" />
            {typeInfo.label}
          </span>

          {/* Name */}
          <span className="text-gray-600 text-xs">{section.name}</span>
        </div>

        {/* Actions (visible on hover) */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {/* Toggle enabled */}
          <button
            onClick={() => onToggleEnabled(!section.enabled)}
            className={`p-1 ${section.enabled ? 'text-green-500' : 'text-gray-400'} hover:text-gray-600`}
            title={section.enabled ? 'Disable' : 'Enable'}
          >
            {section.enabled ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          </button>

          {/* Edit */}
          <button
            onClick={onEdit}
            className="p-1 text-gray-400 hover:text-blue-600"
            title="Edit"
          >
            <Settings2 className="w-3 h-3" />
          </button>

          {/* Delete */}
          <button
            onClick={onDelete}
            className="p-1 text-gray-400 hover:text-red-600"
            title="Delete"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Content (when expanded) - aligned with tag content */}
      {isExpanded && (
        <div className="ml-[52px] pl-4 border-l-2 border-gray-200">
          <SectionContentPreview section={section} />
        </div>
      )}

      {/* Closing tag (when collapsed, show inline) */}
      {!isExpanded && (
        <span className="text-gray-400 text-xs ml-[52px]">...</span>
      )}

      {/* Closing tag (when expanded) - aligned with opening tag */}
      {isExpanded && (
        <div className="flex items-center gap-1 py-1">
          {/* Spacer matching drag handle + expand button */}
          <div className="w-5" />
          <div className="w-5" />
          <span className="text-blue-600">{'</'}</span>
          <span className="text-red-600 font-medium">{tagName}</span>
          <span className="text-blue-600">{'>'}</span>
        </div>
      )}
    </div>
  );
}

// Section content preview based on type
interface SectionContentPreviewProps {
  section: PromptSection;
}

function SectionContentPreview({ section }: SectionContentPreviewProps) {
  const content = getPlaceholderContent(section);
  const typeInfo = SECTION_TYPE_INFO[section.type];

  return (
    <div className="py-2 text-xs space-y-2">
      {/* Attributes as comments */}
      <div className="text-gray-400 space-y-0.5">
        <div>
          <span className="text-gray-500">{'<!-- '}</span>
          <span className="text-purple-500">type</span>
          <span className="text-gray-500">=</span>
          <span className="text-green-600">"{section.type}"</span>
          <span className="text-gray-400 ml-2">({typeInfo.description})</span>
        </div>
        <div>
          <span className="text-gray-500">{'     '}</span>
          <span className="text-purple-500">source</span>
          <span className="text-gray-500">=</span>
          <span className="text-cyan-600">"{section.source}"</span>
          <span className="text-gray-500">{' -->'}</span>
        </div>
      </div>

      {/* Content */}
      {section.template ? (
        <div className="space-y-1">
          <div className="text-amber-600 font-medium">Custom Template:</div>
          <pre className="whitespace-pre-wrap bg-amber-50 p-2 rounded border border-amber-200 text-amber-800">
            {section.template}
          </pre>
        </div>
      ) : (
        <div className="space-y-1">
          <pre className="whitespace-pre-wrap bg-white p-2 rounded border border-gray-200 text-gray-600 italic">
            {content}
          </pre>
          {section.description && (
            <div className="text-gray-400">
              {section.description}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Get placeholder content for preview
function getPlaceholderContent(section: PromptSection): string {
  // If there's a custom template, show it
  if (section.template) {
    return section.template;
  }

  // Get source description from AVAILABLE_SOURCES
  const sourceOptions = AVAILABLE_SOURCES[section.type] || [];
  const sourceOption = sourceOptions.find(opt => opt.value === section.source);

  // Return placeholder based on source
  switch (section.source) {
    case 'config.description':
      return '${agent.description}\n// Agent role and personality description';
    case 'config.instructions':
      return '${agent.instructions}\n// Main instructions for the agent\n// + <skill_instruction> tags (if matched)';
    case 'hook.user_facts':
      return '${user_facts}\n// Permanent user facts from memory\n// e.g., preferences, personality traits';
    case 'hook.birth_chart':
      return '${birth_chart}\n// User astrological birth chart data\n// e.g., sun sign, moon sign, rising sign';
    case 'hook.available_skills':
      return '<available_skills>\n  - annual_report: 年度占星报告 - 生成详细年度运势分析\n  - chart_overview: 星盘概览 - 简洁有趣的星盘解读\n  - daily_fortune: 今日运势 - 每日运势简报\n</available_skills>\n// Always injected to let agent know its capabilities';
    case 'hook.skill_instructions':
      return '<skill_instruction skill="annual_report">\n  Skill-specific instructions here...\n  Only injected when skill is matched.\n</skill_instruction>\n// Dynamically injected based on user intent';
    case 'dependency.dynamic_system_context':
      return '${dynamic_context}\n// Memory context from other hooks\n// e.g., recent conversations, events';
    case 'runtime.time_context':
      return '${current_time}\n// Current date/time information\n// e.g., 2025-12-21 (Sunday) 14:30:00 (Asia/Shanghai)';
    case 'runtime.output_format':
      return '${output_format}\n// Response format instructions\n// e.g., JSON schema, markdown';
    default:
      return sourceOption?.description || `\${${section.source}}`;
  }
}

export default VisualPromptEditor;
